import { Component, Input, OnInit } from '@angular/core';

import { Customer, CustomerProficiency } from '../../../../../../../models/customer';
import { Proficiency } from '../../../../../../../models/proficiency';
import { AccountService } from '../../../../../../../services/account/account.service';
import { ProficiencyService } from '../../../../../../../services/proficiency/proficiency.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { dateStringFromISO8601 } from '../../../../../../../utils';

/**
 * The "Add/Edit Customer Proficiency" modal, opened from the customer profile
 * in one of two modes:
 *   * Add mode  — `customerProficiency` is null; staff pick a proficiency to
 *     grant and an expiration.
 *   * Edit mode — `customerProficiency` is set; staff change that proficiency's
 *     expiration or remove it.
 *
 * The expiration is chosen via `expirationMode` ('never', '1'/'2'/'3' years
 * from today, or 'date' for the exact date in `selectedExpirationDate`).
 */
@Component({
  selector: 'app-add-edit-customer-proficiency-modal',
  templateUrl: './add-edit-customer-proficiency-modal.component.html'
})
export class AddEditCustomerProficiencyModalComponent implements OnInit {

  @Input() customer: Customer;
  // Null in Add mode; the proficiency being edited in Edit mode.
  @Input() customerProficiency: CustomerProficiency = null;
  @Input() savedCallback: () => undefined;

  public selectedProficiencyId: number;

  // The chosen expiration option: 'never', '1'/'2'/'3' (years from today), or
  // 'date' (an exact date entered in `selectedExpirationDate`).
  public expirationMode: string = 'never';
  public selectedExpirationDate: string = null;

  // Add mode only: the proficiencies available to grant.
  public proficiencyList: Proficiency[];

  public loadingProficiencies = false;
  public loadingSubmit = false;
  public errorMessage = '';
  public serverError: number = null;

  public dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public accountService: AccountService,
    private proficiencyService: ProficiencyService,
    public activeModal: NgbActiveModal,
    public customerService: CustomerService
  ) {}

  ngOnInit() {
    if (this.isEdit) {
      // Default to the proficiency's current expiration so that saving without
      // touching the dropdown keeps the existing date rather than clearing it.
      if (this.customerProficiency.expiration_date) {
        this.expirationMode = 'date';
        this.selectedExpirationDate =
          this.customerProficiency.expiration_date.substring(0, 10);
      }
    } else {
      this.loadProficiencies();
    }
  }

  public get isEdit(): boolean {
    return !!this.customerProficiency;
  }

  /**
   * The number of years from today the expiration is set to, or null when the
   * selected mode is not a year-based option.
   */
  public getExpirationYears(): number {
    const years = parseInt(this.expirationMode, 10);
    return isNaN(years) ? null : years;
  }

  /**
   * The exact expiration date, or null when the selected mode is not "date".
   */
  public getExpirationDate(): string {
    return this.expirationMode === 'date' ? this.selectedExpirationDate : null;
  }

  /**
   * Whether the form cannot yet be submitted (the "specific date" option is
   * selected but no date has been chosen).
   */
  public isSaveDisabled(): boolean {
    return this.expirationMode === 'date' && !this.selectedExpirationDate;
  }

  public handleSave() {
    if (this.isSaveDisabled()) {
      return;
    }

    this.loadingSubmit = true;
    this.errorMessage = '';

    const request = this.isEdit
      ? this.customerService.updateCustomerProficiencyExpiration(
          this.customerProficiency.id,
          this.getExpirationYears(),
          this.getExpirationDate()
        )
      : this.customerService.grantCustomerProficiency(
          this.customer.id,
          this.selectedProficiencyId,
          this.getExpirationYears(),
          this.getExpirationDate()
        );

    request
      .then(() => {
        this.savedCallback();
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = this.isEdit
            ? 'You do not have permission to edit customer proficiencies.'
            : 'You do not have permission to create customer proficiencies.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleRemove() {
    this.loadingSubmit = true;
    this.errorMessage = '';

    this.customerService
      .removeCustomerProficiency(this.customerProficiency.id)
      .then(() => {
        this.savedCallback();
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to remove customer proficiencies.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  private loadProficiencies() {
    this.loadingProficiencies = true;

    this.proficiencyService
      .getProficiencies(1, 100)
      .then(response => {
        this.proficiencyList = response.results;
        if (this.proficiencyList.length > 0) {
          this.selectedProficiencyId = this.proficiencyList[0].id;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingProficiencies = false; });
  }

}
